"use client";

import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import styles from "../css/components/smallerSpinner.module.css";

export default function SmallerSpinner({
  value,
  defaultValue = 0,
  min = 0,
  max = 99999999,
  step = 1,
  onChange,
  ariaLabel = "Quantity",
  compact = false,
  className = "",
  style = {},
  btnStyle = {},
  plusStyle = {},
  baseWidth,
}) {
  const [internal, setInternal] = useState(
    typeof value === "number" ? value : Number(defaultValue) || 0
  );
  const current = useMemo(() => (typeof value === "number" ? value : internal), [value, internal]);

  const setValue = (v) => {
    let next = v;
    // Always clamp to min (defaults to 0) and optional max
    if (typeof min === "number") next = Math.max(min, next);
    if (typeof max === "number") next = Math.min(max, next);
    if (typeof value === "number") {
      onChange && onChange(next);
    } else {
      setInternal(next);
      onChange && onChange(next);
    }
  };

  const inc = () => setValue((current || 0) + step);
  const dec = () => setValue((current || 0) - step);

  // Dynamically widen container if digits exceed 2 to avoid clipping
  const numericCurrent = Number.isFinite(current) ? current : 0;
  const integerPart = Math.abs(Math.trunc(numericCurrent));
  const digitCount = String(integerPart).length + (numericCurrent < 0 ? 1 : 0); // include minus sign if any
  // Start expanding when more than 3 digits are present (minus sign counts as 1)
  const threshold = 3;
  const baseMinWidth = typeof baseWidth === "number" ? baseWidth : (compact ? 132 : 148);
  const extraPerDigit = 12;
  const dynamicMinWidth = baseMinWidth + Math.max(0, digitCount - threshold) * extraPerDigit;
  // If a baseWidth is provided, prefer setting an explicit width so pages can truly control baseline length.
  // Respect caller's explicit style.width if provided.
  const widthStyle = typeof baseWidth === "number" && typeof style.width === "undefined"
    ? { width: dynamicMinWidth }
    : {};
  // Let caller override minWidth if they explicitly provide one via style
  const containerStyle = { minWidth: dynamicMinWidth, ...widthStyle, ...style };

  return (
    <div
      className={`${styles.spinWrap} ${compact ? styles.small : ""} ${className}`.trim()}
      role="group"
      aria-label={ariaLabel}
      style={containerStyle}
    >
      <button type="button" className={`${styles.inlineBtn} ${styles.minus}`} onClick={dec} aria-label="Decrease" style={btnStyle}>−</button>
      <input
        type="number"
        className={styles.value}
        aria-live="polite"
        value={Number.isFinite(current) ? current : 0}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === "") {
            // Treat empty as min (default 0) to keep UI stable without adding new visuals
            setValue(typeof min === "number" ? min : 0);
            return;
          }
          // If value starts with 0 (or -0) and is an integer (no decimal), strip leading zeros
          // Examples: "07" -> 7, "000" -> 0, "-0005" -> -5. Keep decimals like "0.5" intact.
          if (/^-?0\d+$/.test(raw)) {
            const sign = raw.startsWith('-') ? -1 : 1;
            const digits = raw.replace(/^-?0+/, '');
            const normalized = digits === '' ? 0 : sign * Number(digits);
            setValue(normalized);
            return;
          }
          const n = Number(raw);
          if (!Number.isNaN(n)) {
            setValue(n);
          }
        }}
        onInput={(e) => {
          const raw = e.target.value;
          if (raw === "") return;
          // Mirror normalization during typing to immediately remove leading zeros
          if (/^-?0\d+$/.test(raw)) {
            const sign = raw.startsWith('-') ? -1 : 1;
            const digits = raw.replace(/^-?0+/, '');
            const normalized = digits === '' ? 0 : sign * Number(digits);
            e.target.value = String(normalized);
            setValue(normalized);
          }
        }}
        onBlur={(e) => {
          const raw = e.target.value;
          if (raw === "") return;
          if (/^-?0\d+$/.test(raw)) {
            const sign = raw.startsWith('-') ? -1 : 1;
            const digits = raw.replace(/^-?0+/, '');
            const normalized = digits === '' ? 0 : sign * Number(digits);
            e.target.value = String(normalized);
            setValue(normalized);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowUp") {
            e.preventDefault();
            inc();
          } else if (e.key === "ArrowDown") {
            e.preventDefault();
            dec();
          }
        }}
        step={step}
        min={min}
        max={typeof max === "number" ? max : undefined}
        inputMode="numeric"
        pattern="[0-9]*"
      />
      <button type="button" className={`${styles.inlineBtn} ${styles.plus}`} onClick={inc} aria-label="Increase" style={{ ...btnStyle, ...plusStyle }}>+</button>
    </div>
  );
}

SmallerSpinner.propTypes = {
  value: PropTypes.number,
  defaultValue: PropTypes.number,
  min: PropTypes.number,
  max: PropTypes.number,
  step: PropTypes.number,
  onChange: PropTypes.func,
  ariaLabel: PropTypes.string,
  compact: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
  btnStyle: PropTypes.object,
  plusStyle: PropTypes.object,
  baseWidth: PropTypes.number,
};
